// Sherik hisobi — bitta ekrandagi amal, serverda BESH chaqiruv.
//
// ⚠️ BITTA ENDPOINT YO'Q va bu taxmin emas, o'qilgan (2026-08-19):
// `AgentController.create` faqat `AgentDraft` ni oladi — `code`,
// `displayName`, `kind`, `settlementMode`, `state`. Login, parol, kontakt,
// depozit va qarz limiti u yerda YO'Q. Shuning uchun zanjir:
//
//   1. POST /api/agents?parentId=…        → tugun (agent moduli)
//   2. POST /api/users                    → login/parol (identity moduli)
//   3. PUT  /api/users/{id}/party-node    → ikkalasini bog'laydi
//   4. PUT  /api/agents/{id}/debt-limit   → qarz limiti
//   5. POST /api/agents/{id}/deposit      → boshlang'ich depozit
//
// ⚠️ ZANJIR ATOMAR EMAS — serverda tranzaksiya yo'q, chunki chaqiruvlar
// IKKI mikroservisga ketadi (ADR-016: modul boshqa modul sxemasiga
// tegmaydi). Demak yarim bajarilgan holat HAQIQIY holat va uni yashirish
// mumkin emas: 1-2 qadam yiqilsa xato chiqadi va hech narsa qolmaydi/
// tugun qoladi, 3-5 qadam yiqilsa esa natija `failed` ro'yxati bilan
// QAYTADI — operator nimasi bajarilmaganini KO'RADI va uni qo'lda
// tugatadi. "Yaratildi" deb yozib, depoziti yo'q agentni qoldirish
// jimgina yolg'on bo'lardi va u kassada, pul kerak bo'lganda chiqardi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { translateError, type Translate } from '@/shared/api/errors'
import { uuidSchema } from '@/shared/api/schemas'
import type { Money } from '@/shared/lib/money'
import {
  AGENT_KINDS,
  SETTLEMENT_MODES,
  agentNodeSchema,
  setCommissionRule,
  toAgentNode,
  type AgentKind,
  type AgentNode,
  type CommissionRuleDraft,
} from './agentApi'

/**
 * Bajarilmagan qadamning nomi — i18n kaliti ham shu
 * (`agents.account.step.<nom>`).
 */
export const ACCOUNT_STEPS = ['onboard', 'compensation', 'commission'] as const
export type AccountStep = (typeof ACCOUNT_STEPS)[number]


const moneyDraftSchema = z.object({ amount: z.number().int(), currency: z.string().min(3) })

/** `platform.contact.contact_phone_shape` chegaralari (V202608640001) */
export const PHONE_MIN = 4
export const PHONE_MAX = 32
export const PHONE_SAFE = /^[^<>&";'\\]+$/

/**
 * Jo'natmaning O'ZI tekshiriladi — javob emas.
 *
 * ⚠️ NEGA SO'ROV TOMONIDA HAM ZOD BOR (panelda bu yagona joy).
 * Zanjir atomar emas, ya'ni 2-qadamdagi rad javobi 1-qadamda yaratilgan
 * tugunni ORQAGA QAYTARMAYDI. Eng ehtimolli rad javobi esa aynan
 * o'lchamlarda: `CreateUserCommand` parolni 12–72 belgi deb cheklaydi
 * (o'qilgan), panelning boshqa formasi esa 128 gacha ruxsat berardi —
 * 100 belgilik parol bilan tugun yaratilib, hisob yaratilmasdan qolardi.
 * Shuning uchun chegara BIRINCHI so'rovdan OLDIN tekshiriladi.
 */
const draftSchema = z.object({
  code: z.string().regex(/^[a-zA-Z0-9._-]{2,64}$/),
  displayName: z.string().trim().min(1).max(200),
  kind: z.enum(AGENT_KINDS),
  settlementMode: z.enum(SETTLEMENT_MODES),
  // ⚠️ `.nullish()`, `.nullable()` EMAS — butun panelda yagona shakl
  // (`tests/null-contract.mjs`). Bu yerda farq amaliy ham: chizilmagan
  // maydon formadan `undefined` bo'lib qaytadi, `null` emas.
  parentId: z.string().min(1).nullish(),
  username: z.string().regex(/^[a-zA-Z0-9._@-]{3,64}$/).nullish(),
  rawPassword: z.string().min(12).max(72).nullish(),
  cashDesk: z.boolean(),
  initialDeposit: moneyDraftSchema.nullish(),
  debtLimit: moneyDraftSchema.nullish(),
  dailyLimit: moneyDraftSchema.nullish(),
  monthlyLimit: moneyDraftSchema.nullish(),
  perTxnLimit: moneyDraftSchema.nullish(),
  contactName: z.string().max(200).nullish(),
  // ⚠️ SHAKL MAJBURLANMAYDI (GEO §7) — tizim Afg'onistonda ham ishlaydi.
  // Qolgani serverdagi `contact_phone_shape` ning aynan o'zi: uzunlik va
  // xavfli belgi yo'qligi.
  contactPhone: z.string().min(PHONE_MIN).max(PHONE_MAX).regex(PHONE_SAFE).nullish(),
  contactNote: z.string().max(500).nullish(),
})

export interface AgentAccountDraft {
  code: string
  /** Ekranda «To'liq ism» — serverda `party_node.display_name` */
  displayName: string
  kind: AgentKind
  settlementMode: 'DEPOSIT' | 'POSTPAID'
  /** `null` — ildiz tugun */
  parentId: string | null
  /**
   * `null` — panel hisobi YARATILMAYDI.
   *
   * Bu `user.create` huquqi yo'q operator uchun: tugun yaratilaveradi,
   * loginni keyinroq huquqi bori qo'shadi. Yolg'on va'da bermaslik
   * uchun forma bunday holatda maydonlarni umuman ko'rsatmaydi.
   */
  username: string | null
  rawPassword: string | null
  /** Faqat `RESELLER` da ma'noli — serverdagi CHECK ham shunday */
  cashDesk: boolean
  initialDeposit: Money | null
  debtLimit: Money | null
  /**
   * Kunlik va oylik PUL limiti (`FundLimitKind`) — `null` CHEKSIZ degani.
   *
   * ⚠️ Abonent amallari chegarasi (`LimitKind`) BILAN chalkashtirilmaydi:
   * u dona sanaydi va resellerga tegishli, bu esa pul va agentga.
   */
  dailyLimit: Money | null
  monthlyLimit: Money | null
  /**
   * Bitta to'lovning shifti (egasi qarori 2026-08-19), sukut 100 000.
   *
   * ⚠️ Kunlik/oylikdan FARQI: bu OYNA emas, ya'ni «sarflandi» degan
   * hisob unda yo'q — har to'ldirish alohida tekshiriladi.
   */
  perTxnLimit: Money | null
  /**
   * Kontakt — `platform.contact` ga yoziladi (ADR-031), tugunga EMAS.
   *
   * ⚠️ `contactName` ODAM ismi, `displayName` esa TUGUN nomi.
   * Ular ko'pincha boshqa va bitta maydonga yig'ilsa ajratib bo'lmasdi.
   */
  contactName: string | null
  contactPhone: string | null
  contactNote: string | null
}

export interface AgentAccountResult {
  /**
   * ⛔ `null` — tugun YARATILMAGAN (`failed` da `onboard` qadami
   * bo'ladi). Server oddiy tekshiruv xatosida ham 200 qaytaradi va
   * tugunni yubormaydi — sxema izohiga qarang.
   */
  node: AgentNode | null
  /** `null` — hisob yaratilmadi (yuqoridagi `username` izohiga qarang) */
  userId: string | null
  /**
   * Bajarilmay qolgan qadamlar va SABABI.
   *
   * ⚠️ Bo'sh bo'lmasa — amal to'liq bajarilmagan. Chaqiruvchi buni
   * KO'RSATISHI shart: tugun yaratilgan, ya'ni xatoni "yaratilmadi" deb
   * ko'rsatish ham yolg'on bo'lardi (operator ikkinchi marta urinib,
   * kod band degan javob olardi).
   */
  failed: readonly { step: AccountStep; reason: string }[]
}

/**
 * Tugun + panel hisobi + pul sozlamalari.
 *
 * ⚠️ TARTIB MUHIM. Tugun BIRINCHI yaratiladi: u — biznes ob'ekti va u
 * bo'lmasa qolgan hech nima ma'noga ega emas. Hisob ikkinchi, chunki
 * uni tugunsiz yaratish "hech qayerga ulanmagan sherik" ni qoldirardi —
 * `PartnerKind.scopes(null)` esa `denyAll` beradi, ya'ni kirgan odam
 * bo'sh panel ko'rardi va sababini bilmasdi.
 *
 * @param idempotencyKey depozit uchun — {@link topUpAgent} talab qiladi
 */
/**
 * `POST /api/agents/onboard` javobi.
 *
 * ⚠️ Server HAR DOIM 200 qaytaradi, natija esa tananing ichida
 * (`result.status`): OK | PARTIAL | FAILED. Bu ataylab — yarim
 * bajarilgan holat texnik xato emas, biznes holati va operator uni
 * ANIQ ko'rishi kerak.
 */
const onboardSchema = z.object({
  result: z.object({
    status: z.string(),
    message: z.string().nullish(),
    detail: z.string().nullish(),
  }),
  /*
    ⛔ `null` — VA BU ODATIY YO'L, xato emas.

    `AgentOnboardResult.failed(...)` tugunni `null` bilan qaytaradi
    (`AgentOnboardResult.java:51`), server esa buni 200 bilan yuboradi
    (`AgentController.onboard`). Ya'ni «bu kod band», «bu login band»,
    «ota tugun topilmadi» degan ENG ODDIY tekshiruvlarda maydon
    javobda umuman bo'lmaydi (`non_null`).

    ⛔ Ilgari bu yerda `agentNodeSchema` MAJBURIY edi va u pastdagi
    "200 kelsa ham yiqilgan bo'lishi mumkin" mantig'ini ISHGA
    TUSHISHIGA QO'YMASDI: `parse` undan oldin istisno tashlardi va
    operator «bu kod band» o'rniga tushunarsiz nosozlikni ko'rardi.
  */
  agent: agentNodeSchema.nullish(),
  userId: uuidSchema.nullish(),
  login: z.string().nullish(),
  depositFunded: z.boolean().nullish(),
  /** `false` — tozalash ham yiqilgan, bazada yarim yozuv QOLGAN */
  compensationClean: z.boolean(),
})

/**
 * Agentni BITTA so'rov bilan ochadi — tugun, kontakt, hisob, depozit,
 * qarz va pul limitlari.
 *
 * <h2>⚠️ Ilgari bu UCH CHAQIRUVLI ZANJIR edi</h2>
 * `POST /api/agents` → `POST /api/users` → `PUT /party-node`, ustiga
 * pul chaqiruvlari. Zanjir ATOMAR emas edi: oraliqda uzilsa tugun
 * yaratilib, hisob yaratilmay qolardi va operator ikkinchi urinishda
 * «kod band» javobini olardi.
 *
 * Server tomonda endi kompensatsiya bor: hisob ochilmasa tugun ham
 * o'chiriladi. Tozalashning O'ZI yiqilsa — `compensationClean: false`.
 */
export async function createAgentAccount(
  draft: AgentAccountDraft,
  idempotencyKey: string,
  t: Translate,
): Promise<AgentAccountResult> {
  draftSchema.parse({ ...draft, displayName: draft.displayName.trim() })

  const response = await api.post<unknown>(
    '/api/agents/onboard',
    {
      code: draft.code.trim(),
      displayName: draft.displayName.trim(),
      parentId: draft.parentId,
      kind: draft.kind,
      settlementMode: draft.settlementMode,
      login: draft.username?.trim() ?? null,
      password: draft.rawPassword,
      fullName: draft.contactName?.trim() || null,
      phone: draft.contactPhone?.trim() || null,
      note: draft.contactNote?.trim() || null,
      cashDesk: draft.cashDesk,
      depositMinor: draft.initialDeposit?.amount ?? null,
      debtLimitMinor: draft.debtLimit?.amount ?? null,
      dailyFundLimitMinor: draft.dailyLimit?.amount ?? null,
      monthlyFundLimitMinor: draft.monthlyLimit?.amount ?? null,
      perTxnFundLimitMinor: draft.perTxnLimit?.amount ?? null,
    },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )

  return parseOnboardResult(response.data, t)
}

/**
 * `POST /api/agents/onboard` javobini panel natijasiga aylantiradi —
 * CHAQIRUV JOYIDAN AJRATILGAN.
 *
 * Sabab `null-contract.mjs` 4-bo'limidagi bilan bir xil: naqsh
 * skaneri faqat matnni ko'radi, ya'ni sxema "to'g'ri" bo'lib turib
 * jonli javobda yiqilishi mumkin. Eksport qilingani uchun qorovul
 * unga serverning AYNI javobini bera oladi
 * (`tests/null-drop-shape.mjs`) — tarmoqsiz.
 */
export function parseOnboardResult(raw: unknown, t: Translate): AgentAccountResult {
  const parsed = onboardSchema.parse(raw)
  const failed: { step: AccountStep; reason: string }[] = []

  /*
    ⚠️ SERVER 200 QAYTARSA HAM AMAL YIQILGAN BO'LISHI MUMKIN: natija
    tananing ichida. Faqat HTTP kodiga qarash "agent ochildi" degan
    yolg'onni ekranga chiqarardi.
  */
  if (parsed.result.status !== 'OK') {
    const serverText = [parsed.result.message, parsed.result.detail].filter(Boolean).join(' — ')
    failed.push({
      step: 'onboard',
      reason: t('common.langCode', { defaultValue: '' }) === 'uz' ? serverText : t('operation.failedTitle'),
    })
  }

  /*
    ⚠️ KOMPENSATSIYA YIQILGANI ALOHIDA XABAR. Odatda `FAILED` da hech
    narsa qolmaydi (server o'zi tozalaydi), lekin tozalash ham yiqilsa
    bazada yarim yozuv qoladi va uni FAQAT odam topa oladi. Uni umumiy
    xato matniga qo'shib yuborish ko'zdan yashirardi.
  */
  if (!parsed.compensationClean) {
    failed.push({ step: 'compensation', reason: t('agents.account.compensationDirty') })
  }

  return {
    node: parsed.agent ? toAgentNode(parsed.agent) : null,
    userId: parsed.userId ?? null,
    failed,
  }
}

/**
 * Agent/reseller ochish + KOMISSIYA qoidasi — bitta ekrandagi amal.
 *
 * <h2>⚠️ IKKI SO'ROV, VA BU MAJBURIY</h2>
 * `POST /api/agents/onboard` komissiyani QABUL QILMAYDI: uning kirish
 * shakli ({@code AgentOnboardRequest}) da `percentBp`, `fixedAmount`
 * yoki `tiers` degan maydon YO'Q. Qoida faqat
 * `PUT /api/agents/{id}/commission-rules` bilan yoziladi, ya'ni agent
 * ALLAQACHON yaratilgan bo'lishi shart — id undan oldin mavjud emas.
 *
 * <h2>⚠️ IKKINCHI SO'ROV YIQILSA AGENT QOLADI</h2>
 * Va aynan shuning uchun xato UMUMIY xato matniga qo'shilmaydi:
 * «yaratilmadi» degan xabar operatorni ikkinchi urinishga, ikkinchi
 * urinish esa «kod band» javobiga olib borardi. Natija `failed`
 * ro'yxatiga ALOHIDA `commission` qadami bo'lib tushadi va ekranda
 * «agent yaratildi, komissiya qo'yilmadi» deb yoziladi.
 *
 * <h2>⚠️ KOMISSIYA IXTIYORIY</h2>
 * `rule === null` bo'lsa ikkinchi so'rov UMUMAN yuborilmaydi — bo'sh
 * qoida yozish «0% komissiya» ni ochib qo'yardi va u umumiy qoidani
 * ham to'sardi (`closeCommissionRule` javadoc'i).
 */
export async function createAgentAccountWithCommission(
  draft: AgentAccountDraft,
  rule: CommissionRuleDraftWithoutAgent | null,
  idempotencyKey: string,
  t: Translate,
): Promise<AgentAccountResult> {
  const created = await createAgentAccount(draft, idempotencyKey, t)
  if (rule === null) return created

  /*
    ⚠️ Onboarding'ning O'ZI yiqilgan bo'lsa komissiya YUBORILMAYDI:
    server `FAILED` da tugunni tozalaydi, ya'ni id yo'q agentga qoida
    yozish ikkinchi, tushunarsiz xato berardi.
  */
  if (created.failed.some((item) => item.step === 'onboard') || created.node === null) {
    return created
  }

  try {
    await setCommissionRule({ ...rule, agentId: created.node.id })
    return created
  } catch (error) {
    return {
      ...created,
      failed: [
        ...created.failed,
        { step: 'commission' as const, reason: translateError(error, t) },
      ],
    }
  }
}

/** `agentId` chaqiruvchidan emas, YARATILGAN tugundan olinadi. */
export type CommissionRuleDraftWithoutAgent = Omit<CommissionRuleDraft, 'agentId'>
